// THE DEBUGGER LANE: what one probe is allowed to be, and the probe fan-out payload.
// No producer yet — plan 102 slice 05 writes it, and `hive-event.test.ts` pins the absence
// by searching this checkout.
// Split out of `../hive-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { count, HIVE_EVENT_MAX_ITEMS, line } from './bounds';
import { HiveEvent } from './kind';
import { HiveRoundPayloadSchema } from './round';

/**
 * What one debugger probe is allowed to be. Read-only by construction: a probe reads the
 * checkout, the journal and the live system, and every one of them is a question, never a
 * path — the debugger lane's leaves are cut by disjoint QUESTIONS.
 */
export const HiveDevopsProbe = {
  /** Reproduce the reported symptom. */
  Reproduce: 'reproduce',
  /** Read the unit journal / container logs around the incident. */
  Logs: 'logs',
  /** Read the live system's own state (cluster, queue, database). */
  LiveSystem: 'live_system',
  /** Find the change that introduced it, by reading history rather than moving the tree. */
  Bisect: 'bisect',
  /** Diff what is deployed against what was expected to be. */
  DeployDiff: 'deploy_diff',
  /** Read configuration and environment as they actually are on the box. */
  ConfigEnv: 'config_env',
} as const;
export type HiveDevopsProbe = (typeof HiveDevopsProbe)[keyof typeof HiveDevopsProbe];

/**
 * THE DEBUGGER LANE'S PROBE FAN-OUT.
 *
 * NO PRODUCER YET — plan 102 slice 05 writes it, and `hive-event.test.ts` pins the absence
 * by SEARCHING the tree. The lane has 27 runs all-time and one serial adapter session; no
 * fan-out event exists, which is why the shape lands before the producer rather than after.
 */
export const DevopsFanOutPayloadSchema = HiveRoundPayloadSchema.extend({
  event: z.literal(HiveEvent.DevopsFanOut),
  /** Which probes this incident was cut into. */
  probes: z
    .array(z.enum(Object.values(HiveDevopsProbe)))
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('hive.md rule 1: the probes the lead cut the incident into — questions, never paths'),
  /**
   * The concurrency ceiling this box was configured with. Deliberately NOT naming the
   * env var that will carry it: slice 05 creates that var, and a name written here first
   * is a doc that lies until then — `packages/config/src/dz-env-vars.test.ts` scans the
   * tree for `DZ_*` and requires a documented row, so naming it early fails the gate too.
   */
  max_probes: z.number().int().min(1).describe('hive.md rule 5: the probe ceiling'),
  /** Questions a probe surfaced and no round was left to dispatch. */
  open_questions: z
    .array(line())
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe(
      'hive.md rule 3: gaps named and left undispatched — the only input to a follow-up round',
    ),
  open_questions_truncated: count().describe('open questions beyond the cap'),
});
export type DevopsFanOutPayload = z.infer<typeof DevopsFanOutPayloadSchema>;
